// Which programmes NRT is available to.
//
// NICOTINE REPLACEMENT THERAPY TREATS NICOTINE DEPENDENCE. That is not a policy
// choice this slice is making; it is what the product is. An alcohol, opioid,
// cannabis, gambling or mood member reaching a "Manage cravings" entry point and
// ordering nicotine gum is not a narrower experience than intended — it is the
// wrong medication offered to someone who did not ask for it.
//
// EXPRESSED AS A SERVER-OWNED KEY SET, mirroring `COST_SHARE_PROGRAM_KEYS`,
// which solves the same problem for the same vocabulary. Three candidates were
// weighed:
//
//   * `program_definitions.domain` — TOO COARSE. The vocabulary is
//     `substance | behavioral | mood`, and alcohol and tobacco are both
//     `substance`. It cannot express this distinction at all.
//
//   * `config.meds_in_scope` — genuinely distinguishes them TODAY: tobacco
//     carries `nrt_patch`/`nrt_gum`/`nrt_lozenge` and alcohol carries
//     `naltrexone`/`acamprosate`/`disulfiram`. Rejected anyway, because that
//     file's own header says the config is "⚠ PLACEHOLDER — clinical + product
//     sign-off pending … illustrative seed data … NOT a Pelago clinical spec …
//     replaced once clinical/product confirm the real formulary". Gating access
//     to a medication on data explicitly marked unreviewed means NRT turns on
//     for a programme the day someone adds `nrt_gum` to its formulary, with
//     nobody reviewing that consequence — and turns OFF just as quietly. It is
//     the same objection D-8 raised to `medication_eligible`.
//
//   * A programme key set — chosen. The gate is legible in the code rather than
//     in seed data, changing it is a reviewed diff, and the cross-check test
//     below makes a renamed key fail loudly instead of silently gating nobody.
//
// ADR-015 does not forbid the literal — but NOT because "this is server-side",
// which is what this note used to say and is not true. The symbols below are
// re-exported from the root `@galapagos/shared` barrel, and `apps/mobile`
// imports that barrel, so `'tobacco'` is reachable from a client bundle
// whatever the only current consumer happens to be (`apps/api`'s
// `nrt-preconditions-repo`). "Server-owned" describes where the DECISION is
// made; it does not describe where the string can end up.
//
// The rule holds for the reason that actually applies: ADR-015 governs client
// logic and copy that varies by condition, and no client reads this value or
// branches on it. The client is handed `not_enrolled_in_programme`, a
// condition-agnostic refusal code, exactly as it was before.
//
// Worth keeping straight, because the wrong version of this sentence is the
// more dangerous artifact: it would tell the next author that anything in this
// file is out of the client's reach, which would be a licence to put something
// there that genuinely must not be.
//
// The cost of the choice, stated: a future tobacco-like programme (a second
// nicotine key, say `vaping`) does NOT gate correctly until someone adds it
// here. That is a deliberate trade — an unreviewed automatic yes to a
// medication is worse than a reviewed manual one.

/**
 * Programme keys whose members may be offered NRT.
 *
 * Keys are the `program_definitions.key` vocabulary; a consuming test
 * cross-checks them against the programme catalogue so a renamed key fails
 * loudly rather than silently gating everyone out.
 */
export const NRT_ELIGIBLE_PROGRAM_KEYS: readonly string[] = Object.freeze(['tobacco']);

/**
 * Is this programme one NRT may be offered for?
 *
 * FAILS CLOSED on anything it cannot resolve. An absent, empty or unknown key
 * is `false`, never a fall-through to "allow" — a member whose enrolment cannot
 * be identified must not be offered medication on the strength of the ambiguity.
 */
export function isNrtEligibleProgramKey(programKey: string | null | undefined): boolean {
  if (typeof programKey !== 'string' || programKey.trim() === '') return false;
  return NRT_ELIGIBLE_PROGRAM_KEYS.includes(programKey);
}
